import { Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ToastService } from '@storybook/services/toast/toast.service';
import { finalize } from 'rxjs';
import { MaxxMemory } from '../maxx-settings.model';
import { MaxxSettingsService, maxxSettingsErrorMessage } from '../maxx-settings.service';

@Component({
  selector: 'pdq-maxx-capabilities-settings',
  templateUrl: './maxx-capabilities-settings.component.html',
  styleUrls: ['./maxx-capabilities-settings.component.scss', '../../settings.scss'],
  standalone: false,
})
export class MaxxCapabilitiesSettingsComponent implements OnInit {
  private readonly maxxSettingsService = inject(MaxxSettingsService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly toastService = inject(ToastService);

  readonly memories = signal<MaxxMemory[]>([]);
  readonly pendingRemovals = signal<ReadonlySet<string>>(new Set());
  readonly isManaging = signal(false);
  readonly isLoading = signal(true);
  readonly isSaving = signal(false);
  readonly loadFailed = signal(false);
  readonly errorMessage = signal<string | null>(null);
  private readonly expandedIds = signal<ReadonlySet<string>>(new Set());
  private readonly shortTexts = signal<ReadonlyMap<string, string | null>>(new Map());

  readonly visibleMemories = computed(() => this.memories().filter((memory) => !this.pendingRemovals().has(memory.id)));
  readonly hasPendingChanges = computed(() => this.pendingRemovals().size > 0);

  ngOnInit(): void {
    this.maxxSettingsService
      .getMemories()
      .pipe(
        finalize(() => this.isLoading.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (memories) => this.memories.set(memories),
        error: (error) => {
          this.loadFailed.set(true);
          this.errorMessage.set(maxxSettingsErrorMessage(error));
        },
      });
  }

  isExpanded(memoryId: string): boolean {
    return this.expandedIds().has(memoryId);
  }

  shortTextOf(memoryId: string): string | null | undefined {
    return this.shortTexts().get(memoryId);
  }

  isShortened(memoryId: string): boolean {
    return typeof this.shortTexts().get(memoryId) === 'string';
  }

  toggleExpanded(memoryId: string): void {
    this.expandedIds.update((ids) => toggled(ids, memoryId, !ids.has(memoryId)));
  }

  setShortText(memoryId: string, shortText: string | null): void {
    if (this.shortTexts().get(memoryId) !== shortText) {
      this.shortTexts.update((texts) => new Map(texts).set(memoryId, shortText));
    }
  }

  remove(memoryId: string): void {
    if (this.isSaving()) return;
    this.pendingRemovals.update((ids) => new Set([...ids, memoryId]));
  }

  clearAll(): void {
    if (this.isSaving()) return;
    this.pendingRemovals.set(new Set(this.memories().map((memory) => memory.id)));
  }

  cancel(): void {
    this.pendingRemovals.set(new Set());
    this.errorMessage.set(null);
  }

  save(): void {
    const recordIds = [...this.pendingRemovals()];
    if (!recordIds.length || this.isSaving()) return;

    this.isSaving.set(true);
    this.maxxSettingsService
      .deleteMemories(recordIds)
      .pipe(
        finalize(() => this.isSaving.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (memories) => {
          this.memories.set(memories);
          this.pendingRemovals.set(new Set());
          this.toastService.showToast({ mode: 'info', message: 'Settings saved successfully' });
        },
        error: () => this.toastService.showToast({ mode: 'danger', message: 'Failed to save settings' }),
      });
  }
}

function toggled(ids: ReadonlySet<string>, id: string, include: boolean): ReadonlySet<string> {
  const next = new Set(ids);
  if (include) next.add(id);
  else next.delete(id);
  return next;
}
